import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useImperativeHandle,
	useState
} from "react";
import { createValidationConfiguration } from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined, isUrl } from "client/Validation/rules";
import {
	conditionalAccessValidator,
	initialConditionalAccessState,
	prepareConditionalAccessForSaving
} from "../ConditionalAccess";
import {
	initialMaintenanceWindowState,
	maintenanceWindowValidator,
	prepareMaintenanceWindowForSaving
} from "../MaintenanceWindow";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { IFormHandler, useFormHandler } from "client/Forms";
import { AppType } from "client/Api/Apps/common";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import {
	FormItem,
	TextInput,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	EditCitrixXenAppViewModel,
	saveCitrixXenAppApp,
	SaveCitrixXenAppModel
} from "client/Api/Apps/EditApp/saveApp";

export type EditCitrixXenappAppProps = {
	initialSettings: EditAppSettings<EditCitrixXenAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditCitrixXenappApp = forwardRef(
	(
		props: EditCitrixXenappAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [formData, setFormData] = useState<SaveCitrixXenAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const { canBeConfigured } = app;

		const formHandler: IFormHandler<SaveCitrixXenAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const prepareModelForSaving = useCallback(
			(model: SaveCitrixXenAppModel): SaveCitrixXenAppModel => {
				return {
					...model,
					serverUrl: canBeConfigured
						? model.serverUrl
						: null,
					appName: canBeConfigured
						? model.appName
						: null,
					maintenanceSettings: prepareMaintenanceWindowForSaving(model.maintenanceSettings),
					conditionalAccessSettings: prepareConditionalAccessForSaving(model.conditionalAccessSettings)
				};
			},
			[canBeConfigured]
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveCitrixXenAppModel>(
			formHandler,
			saveCitrixXenAppApp,
			AppType.CitrixXenApp,
			prepareModelForSaving
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("serverUrl", "appName")
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				{canBeConfigured && (
					<>
						<FormItem
							label={localize("_ServerUrl")}
						>
							<TextInput
								{...formHandler.for("serverUrl")
									.attachToTextInput()
								}
							/>
							<ValidationError
								{...formHandler.for("serverUrl")
									.attachToValidationError()
								}
							/>
						</FormItem>
						<FormItem
							label={localize("_CitrixAppName")}
						>
							<TextInput
								{...formHandler.for("appName")
									.attachToTextInput()
								}
							/>
							<ValidationError
								{...formHandler.for("appName")
									.attachToValidationError()
								}
							/>
						</FormItem>
					</>
				)}
			</EditAppBase>
		);
	}
);
EditCitrixXenappApp.displayName = "EditCitrixXenappApp";

function convertToSaveModel(
	initialSettings: EditAppSettings<EditCitrixXenAppViewModel>
): SaveCitrixXenAppModel {
	const {
		maintenanceWindow,
		conditionalAccessSettings,
		serverUrl,
		appName,
		canBeConfigured
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		serverUrl: canBeConfigured ? serverUrl : "",
		appName: canBeConfigured ? appName : "",
		maintenanceSettings: initialMaintenanceWindowState(maintenanceWindow),
		conditionalAccessSettings: initialConditionalAccessState(conditionalAccessSettings)
	};
}

const validation = createValidationConfiguration<SaveCitrixXenAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("appName")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("serverUrl")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		isUrl({ allowHttpsOnly: true })
	);

validation.forObject("conditionalAccessSettings")
	.addValidators(conditionalAccessValidator());

validation.forObject("maintenanceSettings")
	.addValidators(maintenanceWindowValidator());